import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { getDespesasCategoriasOmie, sincronizarDespesasCategorias } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, GradeKpis, Kpi, Seletor, Vazio, botaoPequeno, estiloTabela, formatData,
  inputStyle, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Categorias do Omie (Fase 2): o lançamento traz só o código da categoria (cCodCateg), que muda
// de empresa para empresa; o código do item do plano vem da DESCRIÇÃO ("2.1.01 Mão de obra PJ").
// Categoria sem código (plano antigo, "A2 - 002.1 - ...") ou com código fora do plano de contas
// da plataforma aparece aqui para corrigir no Omie. Só consulta: a correção é feita no Omie.

export default function CategoriasOmieRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [empresa, setEmpresa] = useState("");
  const [situacao, setSituacao] = useState("");
  const [inativas, setInativas] = useState(false);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [sincronizando, setSincronizando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [erroAcao, setErroAcao] = useState("");
  const { dados, carregando, erro, recarregar } = useCarregar(
    () => getDespesasCategoriasOmie({ empresa, situacao, incluir_inativas: inativas ? "true" : "", busca: buscaAplicada }),
    [empresa, situacao, inativas, buscaAplicada],
  );
  const T = estiloTabela(C);
  const linhas = dados || [];

  const sincronizar = async () => {
    setSincronizando(true);
    setErroAcao("");
    try {
      const r = await sincronizarDespesasCategorias();
      const total = Object.values(r.empresas || {}).reduce((a, e) => a + (e.categorias || 0), 0);
      setAviso(`${total} categorias atualizadas nas ${Object.keys(r.empresas || {}).length} empresas.`);
      recarregar();
    } catch (e) {
      setErroAcao(e.message);
    } finally {
      setSincronizando(false);
    }
  };

  const situacaoDe = (l) => {
    if (!l.codigoItem) return <Badge cor={C.warning} title="A descrição não começa com o código do item (ex.: “2.1.01 …”)">Sem código</Badge>;
    if (!l.noPlano) return <Badge cor={C.muted} title="O código não existe no plano de contas da plataforma">Fora do plano</Badge>;
    return <Badge cor={C.success}>No plano</Badge>;
  };

  return (
    <RealizadoLayout titulo="Categorias do Omie" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
        <Seletor C={C} rotulo="Situação" value={situacao} onChange={setSituacao} opcoes={[
          { value: "", label: "Todas" }, { value: "COM_CODIGO", label: "Com código" },
          { value: "SEM_CODIGO", label: "Sem código" }, { value: "FORA_DO_PLANO", label: "Fora do plano" },
        ]} />
        <Seletor C={C} rotulo="Inativas" value={inativas ? "1" : ""} onChange={(v) => setInativas(v === "1")} opcoes={[
          { value: "", label: "Só ativas" }, { value: "1", label: "Incluir inativas" },
        ]} />
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Buscar
          <input
            value={busca} onChange={(e) => setBusca(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setBuscaAplicada(busca.trim()); }}
            onBlur={() => setBuscaAplicada(busca.trim())} placeholder="Descrição ou código Omie"
            style={{ ...inputStyle(C), minHeight: 36, fontSize: 13, padding: "6px 10px", width: 220 }}
          />
        </label>
        <div style={{ marginLeft: "auto" }}>
          <button style={{ ...botaoPequeno(C.brand), opacity: sincronizando ? 0.6 : 1 }} disabled={sincronizando} onClick={sincronizar}>
            <RefreshCw size={13} style={sincronizando ? { animation: "spin 1s linear infinite" } : undefined} />
            {sincronizando ? "Atualizando…" : "Atualizar categorias do Omie"}
          </button>
        </div>
      </BarraFiltros>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      <AvisoErro C={C} onClose={() => setErroAcao("")}>{erro || erroAcao}</AvisoErro>
      {aviso && <div style={{ fontSize: 12, color: C.success }}>{aviso}</div>}

      {carregando && !dados ? <Carregando C={C} /> : (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo="Categorias de despesa" valor={linhas.length} detalhe="Com os filtros acima" />
            <Kpi C={C} rotulo="Com código no plano" valor={linhas.filter((l) => l.noPlano).length} cor={C.success} />
            <Kpi C={C} rotulo="Fora do plano" valor={linhas.filter((l) => l.codigoItem && !l.noPlano).length} detalhe="Mão de obra, impostos, financeiro…" />
            <Kpi C={C} rotulo="Sem código" valor={linhas.filter((l) => !l.codigoItem).length} detalhe="Plano antigo — lançamento nelas fica sem item" cor={C.warning} />
          </GradeKpis>
          <Card C={C} titulo={`${linhas.length} categoria(s)`} subtitulo="O código do item é lido do início da descrição. Para corrigir, edite a descrição (ou inative a categoria) no Omie e clique em Atualizar.">
            {linhas.length === 0 ? <Vazio C={C}>Nenhuma categoria com esses filtros.</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "62vh" }}>
                <table style={{ ...T.table, minWidth: 1000 }}>
                  <thead><tr>
                    <th style={T.th}>Empresa</th>
                    <th style={T.th}>Código Omie</th>
                    <th style={T.th}>Descrição no Omie</th>
                    <th style={T.th}>Item</th>
                    <th style={T.th}>Nome no plano</th>
                    <th style={T.th}>Situação</th>
                    <th style={T.th}>DRE do Omie</th>
                    <th style={T.th}>Atualizada</th>
                  </tr></thead>
                  <tbody>
                    {linhas.map((l) => (
                      <tr key={l.id} style={l.inativa || l.removidaOmie ? { opacity: 0.55 } : undefined}>
                        <td style={T.td}>{l.empresa}</td>
                        <td style={{ ...T.td, fontVariantNumeric: "tabular-nums" }}>{l.codigoOmie}</td>
                        <td style={T.td}>
                          {l.descricao}
                          {l.inativa && <span style={{ color: C.muted }}> (inativa)</span>}
                          {l.removidaOmie && <span style={{ color: C.danger }}> (removida do Omie)</span>}
                        </td>
                        <td style={{ ...T.td, fontWeight: 700 }}>{l.codigoItem || "—"}</td>
                        <td style={{ ...T.td, color: C.muted }}>{l.nomePlano || "—"}</td>
                        <td style={T.td}>{situacaoDe(l)}</td>
                        <td style={{ ...T.td, color: C.muted }}>{l.codigoDre ? `${l.codigoDre} ${l.descricaoDre || ""}` : "—"}</td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(l.sincronizadoEm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </RealizadoLayout>
  );
}
